import { useState } from "react";
import { Link } from "react-router-dom";
import AnyPeaksSearchPanel from "@/components/reference/AnyPeaksSearchPanel";
import FireTestSampleBrowser from "@/components/reference/FireTestSampleBrowser";
import ReferenceLibraryBrowser from "@/components/reference/ReferenceLibraryBrowser";
import StandardLibraryBrowser from "@/components/reference/StandardLibraryBrowser";
import { useAuth } from "@/hooks/useAuth";
import { useReferenceCompounds } from "@/hooks/useReferenceCompounds";
import { useReferenceDataAvailable } from "@/hooks/useReferenceDataAvailable";
import { useReferenceDBAssets } from "@/hooks/useReferenceDBAssets";
import { anyPeaksApi } from "@/services/api";
import type { StandardAssetSummary } from "@/types/referenceCompound";
import { hasMinRole } from "@/utils/permissions";

// ───────────────────────────── 탭 정의 ─────────────────────────────

type TabKey =
  | "core"
  | "risk"
  | "spectrum"
  | "any-peaks"
  | "standard"
  | "scenario";

/**
 * 표준 라이브러리 탭의 건수 — **활성 asset 이 답한다.**
 *
 * 종전에는 `"30만+ 레코드"` 가 상수로 박혀 있어, 자산이 931,909종으로 바뀐
 * 뒤에도 탭은 계속 30만이라고 말했다. 아직 모르면(로딩·미적재) 건수를
 * 말하지 않는다 — 0 이나 옛 숫자를 대신 놓지 않는다.
 */
function standardSublabel(asset: StandardAssetSummary | null | undefined) {
  if (!asset || asset.record_count == null) return "라이브러리 목록";
  return `${asset.record_count.toLocaleString("ko-KR")}종`;
}

const TABS: { key: TabKey; label: string; sublabel: string }[] = [
  { key: "core", label: "핵심 화합물", sublabel: "60종 큐레이션" },
  { key: "risk", label: "위험 화합물", sublabel: "90여 종" },
  {
    key: "spectrum",
    label: "표준물질 DB",
    sublabel: "중앙대 실측 125종",
  },
  {
    key: "any-peaks",
    label: "m/z 검색",
    sublabel: "이온으로 역검색",
  },
  // 건수는 활성 asset 이 답한다 — 아래 렌더에서 채운다. 상수로 박아 둔
  // "30만+ 레코드" 는 실제 자산이 931,909종이 된 뒤에도 그대로 남아 있었다.
  { key: "standard", label: "표준 라이브러리", sublabel: "" },
  // 2026-10-10 (PLAN 2-11): 화재시험 조건이 입력된 시료 목록 — 물질 목록이 아니다
  { key: "scenario", label: "모의현장 DB", sublabel: "화재시험 시료" },
];

// ───────────────────────────── 화합물 테이블 ─────────────────────────────

// ─────────────────── 큐레이션 목록 빈 상태 (왜 비었는지 말한다) ───────────────────

/**
 * 핵심·위험 목록은 **중앙대 확정 목록 미수신** 상태다(2026-08 기준). 모의현장은
 * 2026-10-10 부터 화재시험 시료 목록(FireTestSampleBrowser)이라 여기 오지 않는다.
 * 빈 화면은 "물질이 없다"로도 "고장났다"로도 읽히므로 이유를 적는다.
 *
 * 단, **관측한 사실과 추정 사유를 갈라 적는다** — 이 문구는 조회가 성공하고
 * 결과가 0건일 때만 나온다(실패는 브라우저의 오류 화면이 맡는다).
 */
function CurationEmptyState({ category }: { category: "core" | "risk" }) {
  const label = {
    core: "핵심 화합물",
    risk: "위험 화합물",
  }[category];
  return (
    <div className="text-center">
      <p className="mb-2 font-medium text-gray-500">데이터 없음</p>
      <p className="text-xs text-gray-400">
        조회는 정상 응답했고 결과가 0건입니다 — {label} 로 분류된 화합물이
        데이터베이스에 없습니다.
      </p>
      <p className="mt-1 text-xs text-gray-400">
        알려진 사유(추정): 연구진 확정 목록 미수신 (2026-08 기준).
      </p>
      <p className="mt-1 text-xs text-gray-400">
        조회 조건: GET /api/v1/reference-compounds?category={category}
      </p>
    </div>
  );
}

// ───────────────────────────── 표준 라이브러리 탭 (asset 기반) ─────────────────────────────

function StandardLibraryTab({
  isAdmin,
  standardAsset,
  isAvailabilityLoading,
}: {
  isAdmin: boolean;
  standardAsset: StandardAssetSummary | null | undefined;
  isAvailabilityLoading?: boolean;
}) {
  // 비관리자는 읽기전용 요약만 조회 — admin 전용 asset 목록 endpoint는 호출하지 않는다(403 방지).
  if (!isAdmin) {
    return (
      <ReadOnlyStandardSummary
        standardAsset={standardAsset ?? null}
        isAvailabilityLoading={isAvailabilityLoading}
      />
    );
  }
  return <AdminStandardLibraryPanel />;
}

// 비관리자 읽기전용 요약 — availability 응답의 standard_asset만 사용.
function ReadOnlyStandardSummary({
  standardAsset,
  isAvailabilityLoading,
}: {
  standardAsset: StandardAssetSummary | null;
  isAvailabilityLoading?: boolean;
}) {
  // availability 가 아직 안 왔을 때 '미적재'로 단정하지 않는다 — 로딩과
  // 부재는 다른 사실이다 (교차 리뷰). 실제 미적재 판정은 목록 endpoint 의
  // 404 가 말한다(브라우저가 그 문구를 갖고 있다).
  if (!standardAsset && isAvailabilityLoading) {
    return (
      <div className="animate-pulse space-y-2 p-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-8 bg-gray-200 rounded" />
        ))}
      </div>
    );
  }
  if (!standardAsset) {
    return (
      <div className="p-12 text-center">
        <p className="text-gray-500 font-medium mb-2">표준 라이브러리 미적재</p>
        <p className="text-xs text-gray-400">
          관리자가 표준 라이브러리를 등록하면 이 곳에 표시됩니다.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* asset 요약 — 한 줄 바. 비관리자도 무엇을 보고 있는지 알아야 한다
          (availability 의 standard_asset = 백엔드가 실제로 쓰는 가장 최근 활성). */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5">
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-600 text-white">
          적재됨
        </span>
        <span className="font-semibold text-gray-900">
          {standardAsset.name}
        </span>
        <span className="text-sm text-gray-500">v{standardAsset.version}</span>
        {standardAsset.record_count !== null && (
          <span className="text-sm text-gray-700">
            · {standardAsset.record_count.toLocaleString("ko-KR")}개 레코드 적재
          </span>
        )}
        <span className="ml-auto text-xs text-gray-500">
          버전 등록·관리는 관리자 메뉴에서 수행됩니다.
        </span>
      </div>

      {/* 2026-08-20 통일: 참조물질 DB 탭과 같은 2단(좌 목록/우 상세).
          위치 이동·포함 검색이 브라우저 안에 있으므로 별도 검색 블록은 없다.
          위 바가 asset 을 이미 말하므로 헤더 라벨은 생략. */}
      <StandardLibraryBrowser showAssetLabel={false} />
    </div>
  );
}

// 관리자 전용 asset 관리 패널 — useReferenceDBAssets(admin endpoint)는 여기서만 호출.
function AdminStandardLibraryPanel() {
  const { data, isLoading, error } = useReferenceDBAssets();

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-2 p-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 bg-gray-200 rounded" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 font-medium">
          asset 목록을 불러오는 중 오류가 발생했습니다.
        </p>
      </div>
    );
  }

  const assets = data?.items ?? [];
  // 백엔드(availability·standard-search·standard-list)는 활성 asset 중
  // **가장 최근(id 최대)** 하나를 쓴다. 목록 정렬(name, id desc)에서 first-match
  // 를 고르면 이름이 다른 활성 asset 이 있을 때 실제 검색 대상과 다른 것을
  // 표시하게 된다 (activate 는 같은 name 안에서만 이전 활성을 해제한다).
  const activeAssets = assets.filter((a) => a.is_active);
  const activeAsset = activeAssets.reduce<
    (typeof activeAssets)[number] | undefined
  >((best, a) => (best === undefined || a.id > best.id ? a : best), undefined);

  return (
    <div className="p-6 space-y-6">
      {/* 활성 asset 요약 */}
      {activeAsset ? (
        // 한 줄로 압축한다(2026-09-06). 정보는 하나도 빼지 않았다 — 목록이
        // 페이지와 함께 흐르게 되면서 위 여백이 그대로 첫 화면의 행 수를
        // 깎기 때문에 세로만 줄인다. 건수는 탭 부제·목록 위치 줄도 말하므로
        // 여기서는 같은 줄에 붙인다.
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-600 text-white">
                활성
              </span>
              <h3 className="font-semibold text-gray-900">
                {activeAsset.name}
              </h3>
              <span className="text-sm text-gray-500">
                v{activeAsset.version}
              </span>
              {activeAsset.record_count !== null && (
                <span className="text-sm text-gray-600">
                  · {activeAsset.record_count.toLocaleString("ko-KR")}개 레코드
                </span>
              )}
              {/* `notes` 는 여기 두지 않는다 — 자산 등록자가 쓰는 내부 메모라
                  프로덕션 실물이 "spectra 3,908,139 / peaks 121,704,228 /
                  1.24GB … 라이선스: docs/2026/260827_….md" 처럼 빌드 수치와
                  저장소 경로를 조회 화면에 그대로 노출하고 있었다(2026-09-06
                  prod 확인). 자산 관리 화면(AdminReferenceDBPage)이 이미
                  같은 값을 보여 준다 — 필요한 사람은 거기서 본다. */}
            </div>
            <Link
              to="/admin/reference-db"
              className="text-xs text-blue-600 hover:underline whitespace-nowrap"
            >
              asset 관리 →
            </Link>
          </div>
          {activeAssets.length > 1 && (
            <p className="mt-2 text-xs text-amber-700">
              ⚠ 활성 asset 이 {activeAssets.length}개입니다 (이름이 다르면 동시
              활성이 가능합니다). 검색·목록은 위에 표시된 가장 최근 asset 하나만
              사용합니다 — 나머지는{" "}
              <Link to="/admin/reference-db" className="underline">
                asset 관리
              </Link>
              에서 정리하세요.
            </p>
          )}
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800 font-medium">활성 asset 없음</p>
          <p className="text-xs text-yellow-700 mt-1">
            표준 라이브러리 asset을 등록하고 활성화하세요.{" "}
            <Link to="/admin/reference-db" className="underline">
              asset 관리
            </Link>
          </p>
        </div>
      )}

      {/* 목록 브라우즈 (원본 Names 방식 — UI 재검토 2026-08-20).
          위 카드가 asset 이름·버전·건수를 이미 말하므로 헤더 라벨은 생략. */}
      <StandardLibraryBrowser showAssetLabel={false} />

      {/* 전체 asset 목록 링크 */}
      <div className="text-center pt-2">
        <Link
          to="/admin/reference-db"
          className="text-sm text-blue-600 hover:underline"
        >
          전체 asset 버전 관리 →
        </Link>
      </div>
    </div>
  );
}

// ───────────────────────────── 메인 페이지 ─────────────────────────────

export default function ReferenceDBPage() {
  const { user } = useAuth();
  // 기본 탭: 데이터가 적재된 표준 라이브러리 — 핵심60/위험90 목록 적재 전
  // 빈 탭이 첫 화면이 되지 않도록 (목록 확정·적재 후 "core"로 되돌릴 것)
  const [activeTab, setActiveTab] = useState<TabKey>("standard");
  const isAdmin = user ? hasMinRole(user.role, "ADMIN") : false;
  const { availability, isLoading: isAvailabilityLoading } =
    useReferenceDataAvailable();

  const {
    data: coreCompounds = [],
    isLoading: coreLoading,
    error: coreError,
  } = useReferenceCompounds({ category: "core", limit: 200 });
  const {
    data: riskCompounds = [],
    isLoading: riskLoading,
    error: riskError,
  } = useReferenceCompounds({ category: "risk", limit: 200 });

  // 실측 스펙트럼 보유 물질. 분류(core/risk)가 아니라 스펙트럼 보유 여부로
  // 조회한다 — 출처 3분류를 플랫폼 계층에 어떻게 대응시킬지는 연구진 결정
  // 사항이라 임의로 채워 넣지 않는다(2026-08-15 회신 요청).
  const {
    data: spectrumCompounds = [],
    isLoading: spectrumLoading,
    error: spectrumError,
  } = useReferenceCompounds({ has_spectrum: true, limit: 500 });

  return (
    /* 전폭 — 스펙트럼 비교는 가로가 정보량이다. 원본(NIST MS Search)도
       화면 전체를 쓴다. 분석 대시보드와 같은 정책(메모리: 대시보드 레이아웃). */
    <div className="w-full px-6 py-6">
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-1">
          참조물질 데이터베이스
        </h1>
        <p className="text-gray-600">
          화재·위해기체 분석에 사용되는 참조 화합물 목록을 카테고리별로
          확인합니다.
        </p>
      </div>

      {/* 탭 */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex gap-0 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex flex-col items-center px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <span>{tab.label}</span>
              <span className="text-xs font-normal mt-0.5 opacity-70">
                {tab.key === "standard"
                  ? standardSublabel(availability?.standard_asset)
                  : tab.sublabel}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* 탭 콘텐츠 */}
      <div className=// `overflow-clip` 이다 — `overflow-hidden` 은 스크롤 컨테이너를 만들어
      // 안쪽의 `position: sticky` 를 죽인다(표준 라이브러리 탭의 조작 줄·상세
      // 패널 고정이 그것 때문에 동작하지 않았다). clip 은 스크롤 컨테이너를
      // 만들지 않으면서 둥근 모서리 clipping 은 그대로 한다.
      "bg-white rounded-lg shadow-sm border border-gray-200 overflow-clip">
        {/* 물질 목록 탭은 전부 같은 2단(좌 목록 / 우 상세)이다 — 2026-08-20 통일.
            종전에는 핵심·위험·모의현장이 표+모달, 표준물질 DB 만 2단이라
            같은 성격의 화면이 탭마다 다른 조작을 요구했다. */}
        {activeTab === "core" && (
          <ReferenceLibraryBrowser
            items={coreCompounds}
            isLoading={coreLoading}
            error={coreError}
            emptyState={<CurationEmptyState category="core" />}
          />
        )}
        {activeTab === "risk" && (
          <ReferenceLibraryBrowser
            items={riskCompounds}
            isLoading={riskLoading}
            error={riskError}
            emptyState={<CurationEmptyState category="risk" />}
          />
        )}
        {activeTab === "spectrum" && (
          /* S10: 원본(NIST MS Search)의 Names 화면 배치 —
             좌측 이름 목록 / 우측 스펙트럼·구조·메타.
             표+모달은 물질을 옮겨 가며 비교하는 흐름을 끊는다. */
          <ReferenceLibraryBrowser
            items={spectrumCompounds}
            isLoading={spectrumLoading}
            error={spectrumError}
            emptyState="실측 스펙트럼을 보유한 참조물질이 없습니다."
          />
        )}
        {activeTab === "any-peaks" && (
          <AnyPeaksSearchPanel
            onSearch={async (request) =>
              (await anyPeaksApi.search(request)).data
            }
          />
        )}
        {activeTab === "standard" && (
          /* 2026-08-20 통일: 표준 라이브러리도 2단(좌 목록/우 상세) —
             모달을 쓰지 않으므로 onRowClick 배선이 없다. */
          <StandardLibraryTab
            isAdmin={isAdmin}
            standardAsset={availability?.standard_asset}
            isAvailabilityLoading={isAvailabilityLoading}
          />
        )}
        {activeTab === "scenario" && (
          /* PLAN 2-11: 화재시험 조건이 입력된 시료 — 총 개수·상세 */
          <FireTestSampleBrowser />
        )}
      </div>
    </div>
  );
}
